import type { IFormFieldValue } from "@agrigate/survey-api/lib/SurveysService/dto";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import {
    Box,
    Button,
    HStack,
    Image,
    Progress,
    Text,
    VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import BackArrowIcon from "./assets/arrow-back-icon.svg?react";
import StarCoin from "./assets/images/star-coin.png";
import type { SurveyQuestion } from "./SurveyBuilderForm";
import useMultiStepForm from "./utils/useMultiStepForm";

interface BaseResponse {
    id: string;
    value: string | string[];
}

export interface SurveyResponse extends BaseResponse {
    levelOneResponses?: LevelOneResponse[];
}

export interface LevelOneResponse extends BaseResponse {
    condition: string;
    levelTwoResponses?: LevelTwoResponse[];
}

interface LevelTwoResponse extends BaseResponse {
    condition: string;
}

const SurveyFormGenerator = (p: {
    surveyId: string;
    formName: string;
    questions: SurveyQuestion[];
    response?: MaybeN<IFormFieldValue[]>;
    rewardCoins?: MaybeN<number>;
    onSubmit: (
        response: SurveyResponse[],
        isComplete?: MaybeN<boolean>,
    ) => void;
    assetBaseUrl: string;
}) => {
    const { formName, questions, onSubmit, response, rewardCoins, surveyId } =
        p;
    const {
        step,
        isFirstStep,
        isLastStep,
        next,
        back,
        answers,
        currentBaseQuestionIndex,
        totalBaseQuestions,
        isCurrentQuestionAnswered,
        isCurrentQuestionRequired,
    } = useMultiStepForm(questions, response);

    const buildSurveyResponse = useCallback(
        (
            questions: SurveyQuestion[],
            answers: Record<string, string | string[]>,
        ): SurveyResponse[] =>
            questions.map((question): SurveyResponse => {
                const baseResponse: SurveyResponse = {
                    id: question.id,
                    value: answers[question.id] ?? "",
                    levelOneResponses: [],
                };

                if (question.levelOneFollowUpQuestions) {
                    question.levelOneFollowUpQuestions.forEach(levelOne => {
                        const baseAnswer = answers[question.id];
                        const condition = levelOne.condition?.id ?? "";
                        let isLevelOneConditionMet = false;

                        if (baseAnswer != null) {
                            isLevelOneConditionMet = Array.isArray(baseAnswer)
                                ? baseAnswer.includes(condition)
                                : baseAnswer === condition;
                        }

                        if (isLevelOneConditionMet) {
                            const levelOneResponse: LevelOneResponse = {
                                id: levelOne.id,
                                value: answers[levelOne.id] ?? "",
                                condition: levelOne.condition?.id ?? "",
                                levelTwoResponses: [],
                            };

                            if (levelOne.levelTwoFollowUpQuestions) {
                                levelOne.levelTwoFollowUpQuestions.forEach(
                                    levelTwo => {
                                        const levelOneAnswer =
                                            answers[levelOne.id];
                                        const levelTwoCondition =
                                            levelTwo.condition?.id ?? "";
                                        let isLevelTwoConditionMet = false;

                                        if (levelOneAnswer != null) {
                                            isLevelTwoConditionMet =
                                                Array.isArray(levelOneAnswer)
                                                    ? levelOneAnswer.includes(
                                                          levelTwoCondition,
                                                      )
                                                    : levelOneAnswer ===
                                                      levelTwoCondition;
                                        }

                                        if (isLevelTwoConditionMet) {
                                            levelOneResponse.levelTwoResponses?.push(
                                                {
                                                    id: levelTwo.id,
                                                    value:
                                                        answers[levelTwo.id] ??
                                                        "",
                                                    condition:
                                                        levelTwo.condition
                                                            ?.id ?? "",
                                                },
                                            );
                                        }
                                    },
                                );
                            }
                            baseResponse.levelOneResponses?.push(
                                levelOneResponse,
                            );
                        }
                    });
                }
                return baseResponse;
            }),
        [],
    );

    const surveyResponse = useMemo(
        () => buildSurveyResponse(questions, answers),
        [questions, answers],
    );

    const progressValue =
        ((currentBaseQuestionIndex + 1) / totalBaseQuestions) * 100;
    return (
        <>
            <HStack justifyContent={"space-between"} alignContent={"center"}>
                <BackArrowIcon height={20} width={20} />
                <Text
                    textAlign={"center"}
                    color={"black"}
                    fontFamily={"Instrument Sans"}
                    fontWeight={600}
                    fontSize={18}
                >
                    {formName}
                </Text>
                <Text>
                    {`${currentBaseQuestionIndex + 1} of ${totalBaseQuestions}`}
                </Text>
            </HStack>
            <VStack height={"90%"} justifyContent={"space-between"}>
                <VStack flexGrow={1} w={"100%"}>
                    <Progress.Root
                        colorPalette={"green"}
                        value={progressValue}
                        style={{ borderRadius: 20 }}
                        w={"100%"}
                        my={5}
                    >
                        <Progress.Track>
                            <Progress.Range />
                        </Progress.Track>
                        <Progress.Label />
                    </Progress.Root>
                    <Box w="100%">{step}</Box>
                </VStack>
                <VStack
                    w={"100%"}
                    borderTopWidth={1}
                    borderTopColor={"#CACACA"}
                    borderStyle={"dashed"}
                    pb={20}
                >
                    {rewardCoins && rewardCoins > 0 ? (
                        <HStack
                            backgroundColor={"#FFF9D8"}
                            borderColor={"#F0E9BF"}
                            borderWidth={1}
                            p={2}
                            borderRadius={100}
                            m={5}
                        >
                            <Text mb={0}>Finish this survey to earn</Text>
                            <Image src={StarCoin} />
                            <Text mb={0}>{rewardCoins}</Text>
                        </HStack>
                    ) : null}
                    <HStack w={"100%"} mt={4}>
                        <Button
                            type="button"
                            onClick={back}
                            disabled={isFirstStep}
                            flex={1}
                            p={5}
                            bgColor={"white"}
                            borderColor={"#C5C5C5"}
                            color={"#C5C5C5"}
                            borderWidth={1}
                        >
                            <Text color={"#C5C5C5"} m={0}>
                                {"< Back"}
                            </Text>
                        </Button>
                        {isLastStep ? (
                            <Button
                                flex={1}
                                type="button"
                                onClick={() => {
                                    onSubmit(surveyResponse, true);
                                }}
                                bgColor={"#2A8645"}
                                color={"white"}
                                p={5}
                                disabled={
                                    isCurrentQuestionRequired &&
                                    !isCurrentQuestionAnswered
                                }
                            >
                                Submit
                            </Button>
                        ) : (
                            <Button
                                flex={1}
                                type="button"
                                onClick={() => {
                                    onSubmit(surveyResponse);
                                    next();
                                }}
                                bgColor={"#2A8645"}
                                color={"white"}
                                p={5}
                                disabled={
                                    isCurrentQuestionRequired &&
                                    !isCurrentQuestionAnswered
                                }
                            >
                                {"Next >"}
                            </Button>
                        )}
                    </HStack>
                </VStack>
            </VStack>
        </>
    );
};

export default SurveyFormGenerator;
